import { useEffect, useRef } from 'react';
import { useSsoReauth } from '../contexts/SsoReauthContext';
import { SSO_REAUTH_REQUIRED_CODE, type RequestError } from '../utils/requestErrors';
import { getFacilitiesByOrganization } from './useFacilities';
import { getMarkets } from './useMarkets';
import { getSsoCredentials } from './useSsoCredentials';
import { getSsoSessions } from './useSsoSessions';

type MetrcSignInCheck = {
  organizationId?: number | string | null;
  // The selected package's market. Without one, an organization with a single SSO market is checked.
  marketCode?: string | null;
  // The selected package's RetailID facility. Every Metrc call is facility-level, so nothing is checked until one is known.
  facilityId?: number | string | null;
  enabled?: boolean;
};

const FACILITY_LOOKUP_PAGE_SIZE = 100;
const FACILITY_LOOKUP_MAX_PAGES = 20;

// The MCA (Metrc) facility id of a RetailID facility, or null when it cannot be found.
async function findMcaFacilityId(organizationId: number, facilityId: number): Promise<number | null> {
  for (let page = 0; page < FACILITY_LOOKUP_MAX_PAGES; page += 1) {
    const result = await getFacilitiesByOrganization(organizationId, page, FACILITY_LOOKUP_PAGE_SIZE);
    const match = result.data.find((facility) => Number(facility.id) === facilityId);
    if (match) {
      const mcaFacilityId = Number(match.metrcFacilityId);
      return Number.isSafeInteger(mcaFacilityId) && mcaFacilityId > 0 ? mcaFacilityId : null;
    }
    if (result.data.length < FACILITY_LOOKUP_PAGE_SIZE) return null;
  }
  return null;
}

// The same 409 body the API sends when no Metrc sign-in works, so the shared prompt handles it.
function signInRequired(organizationId: number, marketId: number, canSignIn: boolean): RequestError {
  const error = new Error('Metrc sign-in is required.') as RequestError;
  error.status = 409;
  error.body = {
    code: SSO_REAUTH_REQUIRED_CODE,
    message: 'Your Metrc sign-in has expired. Sign in to Metrc before printing.',
    canSignIn,
    organizationId,
    marketId,
    contacts: [],
  };
  return error;
}

// RID-3165: shows the Metrc sign-in prompt when the print screen opens (or a package is picked) and the market has no
// usable sign-in, instead of the user finding out only after clicking Print. Informational: the server still checks
// when the print is submitted, and any failure here is ignored.
export default function useMetrcSignInCheck({ organizationId, marketCode, facilityId, enabled = true }: MetrcSignInCheck): void {
  const { handleSsoError } = useSsoReauth();
  const prompted = useRef(new Set<string>());

  useEffect(() => {
    const orgId = Number(organizationId);
    const ridFacilityId = Number(facilityId);
    if (!enabled || !Number.isSafeInteger(orgId) || orgId <= 0) return undefined;
    // Every Metrc call is facility-level; with no package picked there is no sign-in to check yet (the server still checks on print).
    if (!Number.isSafeInteger(ridFacilityId) || ridFacilityId <= 0) return undefined;
    let cancelled = false;

    (async () => {
      const ssoMarkets = (await getMarkets(orgId)).filter((market) => market.ssoBaseUrl && market.ssoClientId);
      const code = marketCode?.trim().toUpperCase();
      const market = code
        ? ssoMarkets.find((item) => item.code?.trim().toUpperCase() === code)
        : ssoMarkets.length === 1 ? ssoMarkets[0] : undefined;
      const marketId = Number(market?.id);
      if (cancelled || !Number.isSafeInteger(marketId) || marketId <= 0) return;

      const key = `${orgId}:${marketId}:${ridFacilityId}`;
      if (prompted.current.has(key)) return;

      const mcaFacilityId = await findMcaFacilityId(orgId, ridFacilityId);
      if (cancelled || mcaFacilityId === null) return;

      const credentials = await getSsoCredentials({ organizationId: orgId, marketId, mcaFacilityId });
      if (cancelled || !credentials.ssoEnabled || !credentials.requiresSsoAuthentication) return;

      const canSignIn = await getSsoSessions().then((sessions) => sessions.isMcaUser === true, () => false);
      if (cancelled || prompted.current.has(key)) return;
      prompted.current.add(key);
      handleSsoError(signInRequired(orgId, marketId, canSignIn));
    })().catch(() => undefined);

    return () => { cancelled = true; };
  }, [enabled, organizationId, marketCode, facilityId, handleSsoError]);
}
